import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Center, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { ArticleGridCard } from '@src/components/sco/ArticleGrid/ArticleGridCard';
import { GridPagination } from '@src/components/sco/GridPagination';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { ArticleOrderByInput, ArticleType } from '@src/graphql-types';
import { gql, gqlArgs, gqlVars } from '@src/lib/gql';
import { parseAsInteger, useQueryState } from 'nuqs';
import React from 'react';
const PAGE_SIZE = 24;
export type ArticleGridFilters = {
  authorSlugs?: string[];
  tagSlugs?: string[];
  type?: ArticleType;
};
export type ArticleGridProps = {
  filters?: ArticleGridFilters;
  order?: ArticleOrderByInput;
  pageSize?: number;
  baseUrl?: string;
  columns?: number;
};
export const ArticleGrid = props => {
  const $ = _c(45);
  const pageSize = props.pageSize ?? PAGE_SIZE;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsInteger.withDefault(1);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [page] = useQueryState("page", t0);
  const currentPage = Math.max(1, page || 1);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t1;
  if ($[1] !== props.filters || $[2] !== props.order) {
    t1 = ArticleGrid.queries.articles(props.filters, props.order);
    $[1] = props.filters;
    $[2] = props.order;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = props.filters?.authorSlugs;
  const t3 = (currentPage - 1) * pageSize;
  const t4 = props.filters?.type;
  const t5 = props.filters?.tagSlugs;
  let t6;
  if ($[4] !== pageSize || $[5] !== props.order || $[6] !== t2 || $[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = {
      variables: {
        authorSlugs: t2,
        first: pageSize,
        skip: t3,
        type: t4,
        tagSlugs: t5,
        order: props.order
      }
    };
    $[4] = pageSize;
    $[5] = props.order;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const query = useQuery(t1, t6);
  if (query.loading) {
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Center padding={4}><Spinner /></Center>;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  if (query.error) {
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Center padding={4}><Text color="red.500" textAlign="center">Failed to load articles.</Text></Center>;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  const connection = query.data?.articlesConnection;
  let T0;
  let t10;
  let t11;
  let t7;
  let t8;
  let t9;
  let totalPages;
  if ($[13] !== connection?.aggregate?.count || $[14] !== connection?.edges || $[15] !== getBreakpointValue || $[16] !== pageSize || $[17] !== props.baseUrl || $[18] !== props.columns || $[19] !== query.loading) {
    t11 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const articles = connection?.edges?.map(_temp) ?? [];
      const totalCount = connection?.aggregate?.count ?? 0;
      totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      if (!query.loading && articles.length === 0) {
        let t12;
        if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
          t12 = <Center py={16}><Text color="primary" textStyle="labelMd">No articles found.</Text></Center>;
          $[27] = t12;
        } else {
          t12 = $[27];
        }
        t11 = t12;
        break bb0;
      }
      T0 = SimpleGrid;
      const t12 = props.columns ?? 3;
      if ($[28] !== getBreakpointValue || $[29] !== t12) {
        t7 = getBreakpointValue([1, 1, 2, t12]);
        $[28] = getBreakpointValue;
        $[29] = t12;
        $[30] = t7;
      } else {
        t7 = $[30];
      }
      t8 = 4;
      t9 = "full";
      let t13;
      if ($[31] !== props.baseUrl) {
        t13 = article => <ArticleGridCard key={article.id} article={article} baseUrl={props.baseUrl} />;
        $[31] = props.baseUrl;
        $[32] = t13;
      } else {
        t13 = $[32];
      }
      t10 = articles.map(t13);
    }
    $[13] = connection?.aggregate?.count;
    $[14] = connection?.edges;
    $[15] = getBreakpointValue;
    $[16] = pageSize;
    $[17] = props.baseUrl;
    $[18] = props.columns;
    $[19] = query.loading;
    $[20] = T0;
    $[21] = t10;
    $[22] = t11;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = totalPages;
  } else {
    T0 = $[20];
    t10 = $[21];
    t11 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
    totalPages = $[26];
  }
  if (t11 !== Symbol.for("react.early_return_sentinel")) {
    return t11;
  }
  let t12;
  if ($[33] !== T0 || $[34] !== t10 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t12 = <T0 columns={t7} gap={t8} width={t9}>{t10}</T0>;
    $[33] = T0;
    $[34] = t10;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== currentPage || $[40] !== totalPages) {
    t13 = <GridPagination page={currentPage} totalPages={totalPages} />;
    $[39] = currentPage;
    $[40] = totalPages;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t12 || $[43] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  return t14;
};
ArticleGrid.queries = {
  articles: (filters?: ArticleGridFilters, order?: ArticleOrderByInput) => {
    const vars = gqlVars({
      '$first: Int': true,
      '$skip: Int': true,
      '$type: ArticleType': filters?.type,
      '$tagSlugs: [String]': filters?.tagSlugs?.length,
      '$authorSlugs: [String]': filters?.authorSlugs?.length,
      '$order: ArticleOrderByInput': order
    });
    const where = gqlArgs({
      'where: ': {
        'type: $type': filters?.type,
        'terms_some: { slug_in: $tagSlugs }': filters?.tagSlugs?.length,
        'authors_some: { slug_in: $authorSlugs }': filters?.authorSlugs?.length
      }
    });
    return gql`
      query ${vars} {
        articlesConnection(
          first: $first
          skip: $skip
          orderBy: ${order ? '$order' : 'publishedAt_DESC'}
          ${where}
        ) {
          aggregate {
            count
          }
          edges {
            node {
              id
              slug
              type
              title
              seoDescription
              originalPublicationDate
              featuredImage {
                id
                url(transformation: { image: { resize: { fit: max, width: 800 } } })
              }
              authors {
                id
                firstName
                lastName
                avatar {
                  id
                  url
                }
              }
              terms {
                id
                name
                slug
              }
            }
          }
        }
      }
    `;
  }
};
function _temp(e) {
  return e.node;
}